import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import useVirtual from "react-cool-virtual";

import { useListContext } from "./List.hooks";
import { ListItemGroup } from "./ListItemGroup";

import { Separator } from "@/components/ui/separator";
import { cn } from "@/components/ui/utils";

/** The group a row belongs to, headed where its run of rows begins; ``null`` rows go unheaded. */
export type ListItemGroupOf<T> = (
  item: T,
) => { key: string; label: React.ReactNode } | null;

function checkedBoundaryIndex<T>(
  data: T[],
  checkedRows: Record<string | number, boolean>,
  getRowKey: (item: T) => string | number,
): number {
  let i = 0;
  while (i < data.length && checkedRows[getRowKey(data[i])]) i++;
  return i > 0 && i < data.length ? i : -1;
}

function CheckedDivider() {
  return <Separator className="my-0.5" />;
}

function VirtualizedListItems<T = unknown>({
  children,
  uncheckedData,
  classes,
  activeIndex,
  checkedRows,
  getRowKey,
  setContextItemHeight,
  overscanCount,
  dividerIndex,
}: {
  children: (
    item: T,
    index: number,
    isActive?: boolean,
    isChecked?: boolean,
  ) => React.ReactNode;
  uncheckedData: T[];
  classes: { listItems: string };
  activeIndex: number | null;
  checkedRows: Record<string | number, boolean>;
  getRowKey: (item: T) => string | number;
  setContextItemHeight: (height: number) => void;
  overscanCount: number;
  dividerIndex: number;
}) {
  const firstItemRef = useRef<HTMLDivElement>(null);
  const [localItemHeight, setLocalItemHeight] = useState<number>(32);

  const {
    outerRef,
    innerRef,
    items: virtualItems,
  } = useVirtual({
    itemCount: uncheckedData.length,
    itemSize: localItemHeight,
    scrollDuration: 0,
    overscanCount,
  });

  useLayoutEffect(() => {
    if (firstItemRef.current) {
      const height = firstItemRef.current.getBoundingClientRect().height;
      if (height > 0) {
        setLocalItemHeight(height);
        setContextItemHeight(height);
      }
    }
  }, [uncheckedData.length, setContextItemHeight]);

  return (
    <div
      ref={outerRef as React.RefObject<HTMLDivElement>}
      className={cn(uncheckedData.length > 0 && classes.listItems)}
    >
      <div
        ref={innerRef as React.RefObject<HTMLDivElement>}
        className="relative w-full"
      >
        {virtualItems.map((virtualItem) => {
          const { index, measureRef } = virtualItem;
          const item = uncheckedData[index];

          if (!item) return null;

          const isFirstItem = index === 0;

          return (
            <div
              key={getRowKey(item)}
              ref={(node: HTMLDivElement | null) => {
                measureRef(node as HTMLElement);
                if (isFirstItem && node) {
                  (
                    firstItemRef as React.MutableRefObject<HTMLDivElement | null>
                  ).current = node;
                }
              }}
            >
              {index === dividerIndex && <CheckedDivider />}
              {children(
                item,
                index,
                activeIndex === index,
                checkedRows[getRowKey(item)],
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DirectListItems<T = unknown>({
  children,
  uncheckedData,
  classes,
  activeIndex,
  checkedRows,
  getRowKey,
  setContextItemHeight,
  dividerIndex,
  getGroup,
}: {
  children: (
    item: T,
    index: number,
    isActive?: boolean,
    isChecked?: boolean,
  ) => React.ReactNode;
  uncheckedData: T[];
  classes: { listItems: string };
  activeIndex: number | null;
  checkedRows: Record<string | number, boolean>;
  getRowKey: (item: T) => string | number;
  setContextItemHeight: (height: number) => void;
  dividerIndex: number;
  getGroup?: ListItemGroupOf<T>;
}) {
  const firstItemRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (firstItemRef.current) {
      const height = firstItemRef.current.getBoundingClientRect().height;
      if (height > 0) {
        setContextItemHeight(height);
      }
    }
  }, [uncheckedData.length, setContextItemHeight]);

  return (
    <div className={cn(uncheckedData.length > 0 && classes.listItems)}>
      {uncheckedData.map((item, index) => {
        const isFirstItem = index === 0;
        const group = getGroup?.(item) ?? null;
        const previousGroup =
          index > 0 ? (getGroup?.(uncheckedData[index - 1]) ?? null) : null;

        return (
          <React.Fragment key={getRowKey(item)}>
            {group && group.key !== previousGroup?.key && (
              <ListItemGroup value={group.label} isSticky />
            )}
            <div ref={isFirstItem ? firstItemRef : undefined}>
              {index === dividerIndex && <CheckedDivider />}
              {children(
                item,
                index,
                activeIndex === index,
                checkedRows[getRowKey(item)],
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function ListItems<T = unknown>({
  children,
  getGroup,
}: {
  children: (
    item: T,
    index: number,
    isActive?: boolean,
    isChecked?: boolean,
  ) => React.ReactNode;
  /**
   * Heads each run of rows in a group with a sticky heading. Grouped rows
   * render unvirtualized: a sticky heading only sticks among its siblings.
   */
  getGroup?: ListItemGroupOf<T>;
}) {
  const {
    uncheckedData,
    classes,
    activeIndex,
    checkedRows,
    getRowKey,
    setItemHeight: setContextItemHeight,
    overscanCount,
  } = useListContext<T>();

  const dividerIndex = useMemo(
    () => checkedBoundaryIndex(uncheckedData, checkedRows, getRowKey),
    [uncheckedData, checkedRows, getRowKey],
  );

  if (uncheckedData.length === 0) {
    return null;
  }

  // Skip virtualization when overscanCount covers all items —
  // this avoids the flash caused by react-cool-virtual returning
  // 0 items on mount before ResizeObserver fires.
  const skipVirtualization =
    getGroup !== undefined || overscanCount >= uncheckedData.length;

  if (skipVirtualization) {
    return (
      <DirectListItems
        uncheckedData={uncheckedData}
        classes={classes}
        activeIndex={activeIndex}
        checkedRows={checkedRows}
        getRowKey={getRowKey}
        setContextItemHeight={setContextItemHeight}
        dividerIndex={dividerIndex}
        getGroup={getGroup}
      >
        {children}
      </DirectListItems>
    );
  }

  return (
    <VirtualizedListItems
      uncheckedData={uncheckedData}
      classes={classes}
      activeIndex={activeIndex}
      checkedRows={checkedRows}
      getRowKey={getRowKey}
      setContextItemHeight={setContextItemHeight}
      overscanCount={overscanCount}
      dividerIndex={dividerIndex}
    >
      {children}
    </VirtualizedListItems>
  );
}
